import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { listInvoices, type Invoice } from '../../api/invoices'
import { DataTable, type Column } from '@/components/DataTable'
import { FilterBar } from '@/components/FilterBar'
import { PageHeader } from '@/components/PageHeader'
import { StatusBadge } from '@/components/StatusBadge'

const INVOICE_FILTERS = [
  { id: 'alle', label: 'Alle' },
  { id: 'offen', label: 'Offen' },
  { id: 'bezahlt', label: 'Bezahlt' },
  { id: 'storniert', label: 'Storniert' },
]

export default function InvoiceList() {
  const navigate = useNavigate()
  const [activeFilter, setActiveFilter] = useState('alle')
  const [search, setSearch] = useState('')
  const [minId, setMinId] = useState<number | undefined>()
  const [pageHistory, setPageHistory] = useState<number[]>([])

  function handleFilterChange(id: string) {
    setActiveFilter(id)
    setMinId(undefined)
    setPageHistory([])
  }

  function handleSearch(value: string) {
    setSearch(value)
    setMinId(undefined)
    setPageHistory([])
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invoices', activeFilter, search, minId],
    queryFn: () =>
      listInvoices({
        status: activeFilter !== 'alle' ? activeFilter : undefined,
        search: search || undefined,
        min_id: minId,
      }),
    staleTime: 30_000,
  })

  const columns: Column<Invoice>[] = [
    {
      key: 'number',
      header: 'Rechnungsnummer',
      className: 'font-mono text-xs font-medium',
    },
    {
      key: 'order_id',
      header: 'Auftrag',
      cell: (inv) => inv.order_id === null ? '—' : `#${inv.order_id}`,
    },
    {
      key: 'date',
      header: 'Datum',
      cell: (inv) => inv.date ? new Date(inv.date).toLocaleDateString('de-DE') : '—',
    },
    {
      key: 'total',
      header: 'Betrag',
      className: 'text-right font-mono',
      cell: (inv) => inv.total === null ? '—' : `${Number(inv.total).toFixed(2)} €`,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (inv) => <StatusBadge status={inv.status} />,
    },
  ]

  return (
    <div className="p-6">
      <PageHeader title="Rechnungen" />
      <FilterBar
        filters={INVOICE_FILTERS}
        active={activeFilter}
        onChange={handleFilterChange}
        search={search}
        onSearch={handleSearch}
        placeholder="Rechnungsnummer, Auftragsnummer…"
      />
      <div className="mt-4">
        {isError && (
          <p className="text-sm text-destructive mb-3">Fehler beim Laden.</p>
        )}
        <DataTable
          data={data?.invoices ?? []}
          columns={columns}
          rowKey={(inv) => inv.id}
          onRowClick={(inv) => navigate(`/invoices/${inv.id}`)}
          isLoading={isLoading}
          emptyMessage="Keine Rechnungen gefunden."
          pagination={data ? {
            hasMore: data.has_more,
            onNext: () => {
              if (data.next_min_id) {
                setPageHistory((h) => [...h, minId ?? 0])
                setMinId(data.next_min_id)
              }
            },
            onPrev: () => {
              const prev = pageHistory[pageHistory.length - 1]
              setPageHistory((h) => h.slice(0, -1))
              setMinId(prev === 0 ? undefined : prev)
            },
            canPrev: pageHistory.length > 0,
            count: data.invoices.length,
          } : undefined}
        />
      </div>
    </div>
  )
}
